"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.checkCouponCodeResponseSchema = exports.checkCouponCodeBodySchema = exports.checkCouponCodeParamsSchema = exports.checkCashierCodeResponseSchema = exports.checkCashierCodeBodySchema = exports.checkCashierCodeParamsSchema = exports.createRefundResponseSchema = exports.createRefundBodySchema = exports.listRedemptionsResponseSchema = exports.listRedemptionsQuerySchema = exports.createRedemptionResponseSchema = exports.createRedemptionBodySchema = exports.createRefundParamsSchema = exports.listRedemptionsParamsSchema = exports.createRedemptionParamsSchema = exports.CouponRedemptionEntitySchema = void 0;
const zod_1 = require("zod");
const wallet_schemas_1 = require("../customer/wallet.schemas");
const common_schemas_1 = require("../base/common.schemas");
const api_schemas_1 = require("../base/api.schemas");
const timestamp_schemas_1 = require("../base/timestamp.schemas");
// ============================================
// INTERNAL SCHEMAS (Entity)
// ============================================
// Coupon redemption entity schema matching database coupon_redemptions table
const _CouponRedemptionBaseSchema = zod_1.z.object({
    redemption_id: zod_1.z.number().int().positive(),
    coupon_id: zod_1.z.number().int().positive(),
    user_coupon_id: zod_1.z.number().int().positive().nullable(),
    user_id: common_schemas_1.uuidSchema.nullable(),
    sharing_id: zod_1.z.number().int().positive().nullable(),
    sharer_user_id: common_schemas_1.uuidSchema.nullable(),
    bill_amount: common_schemas_1.moneyAmountSchema,
    discount_amount: common_schemas_1.moneyAmountSchema,
    final_amount: common_schemas_1.moneyAmountSchema,
    invoice_id: zod_1.z.string().max(50).nullable(),
    cashier_code: zod_1.z.string().max(4).nullable(),
    redeemed_by_staff_id: common_schemas_1.uuidSchema.nullable(),
    redemption_notes: zod_1.z.string().nullable(),
    redeemed_at: timestamp_schemas_1.timestampIsoSchema,
});
// ============================================
// ENTITY SCHEMAS (Export Aliases)
// ============================================
exports.CouponRedemptionEntitySchema = _CouponRedemptionBaseSchema;
// ============================================
// MERCHANT REDEMPTION API SCHEMAS
// ============================================
// Common merchant params schema
const _MerchantParamsSchema = zod_1.z.object({
    merchantId: zod_1.z.coerce.number().int().positive(),
});
// Redemption params schema (merchantId + redemptionId)
const _RedemptionParamsSchema = _MerchantParamsSchema.extend({
    redemptionId: zod_1.z.coerce.number().int().positive(),
});
// Params schemas for each endpoint
exports.createRedemptionParamsSchema = _MerchantParamsSchema;
exports.listRedemptionsParamsSchema = _MerchantParamsSchema;
exports.createRefundParamsSchema = _RedemptionParamsSchema;
// POST /merchants/:merchantId/redemption/process - Body (FR56-64, FR190-196)
//
// REDEMPTION FLOW (per PRD):
// 1. Cashier enters 4-digit code FIRST (FR190-192)
// 2. If invalid, block immediately (FR192)
// 3. Scan/enter customer coupon code (FR56)
// 4. Enter purchase amount (FR57)
// 5. System validates minimum purchase AFTER amount entered (FR196)
// 6. Apply credit if customer chooses (FR68-69)
// 7. Process and record (FR64)
//
exports.createRedemptionBodySchema = zod_1.z.object({
    cashier_code: zod_1.z.string().length(4).regex(/^\d{4}$/), // 4-digit code (FR186)
    coupon_code: zod_1.z.string().min(1).max(10), // Customer's coupon code (4-digit numeric)
    purchase_amount: common_schemas_1.moneyAmountSchema.positive(),
    invoice_id: zod_1.z.string().trim().min(1).max(50), // FR193
    note: zod_1.z.string().max(500).optional(), // FR194
    apply_credit: zod_1.z.boolean().default(false), // FR68
    credit_amount: common_schemas_1.moneyAmountSchema.optional(), // Amount of credit to apply
});
// POST /merchants/:merchantId/redemption/process - Response
exports.createRedemptionResponseSchema = (0, api_schemas_1.dataResponseSchema)(zod_1.z.object({
    redemption_id: zod_1.z.number().int(),
    original_amount: common_schemas_1.moneyAmountSchema,
    discount_amount: common_schemas_1.moneyAmountSchema, // From coupon
    credit_applied: common_schemas_1.moneyAmountSchema, // From credit balance
    final_amount: common_schemas_1.moneyAmountSchema,
    cashier_name: zod_1.z.string(),
    timestamp: timestamp_schemas_1.timestampIsoSchema,
    code_type: zod_1.z.enum(['self', 'shared']).optional(), // V1.2: Dual code path indicator
}));
// GET /merchants/:merchantId/redemption/history - Query
exports.listRedemptionsQuerySchema = zod_1.z.object({
    startDate: timestamp_schemas_1.timestampIsoSchema.optional(),
    endDate: timestamp_schemas_1.timestampIsoSchema.optional(),
    userId: common_schemas_1.uuidSchema.optional(),
    limit: zod_1.z.coerce.number().int().min(1).max(100).default(50),
    cursor: zod_1.z.string().optional(),
});
// Internal schema for redemption item in list
const _RedemptionItemSchema = zod_1.z.object({
    redemption_id: zod_1.z.number().int(),
    coupon_id: zod_1.z.number().int().positive(),
    user_id: common_schemas_1.uuidSchema.nullable(),
    user_display_name: zod_1.z.string().nullable(),
    coupon_title: zod_1.z.string(),
    coupon_type: wallet_schemas_1.CouponTypeSchema,
    original_amount: common_schemas_1.moneyAmountSchema,
    discount_amount: common_schemas_1.moneyAmountSchema,
    credit_applied: common_schemas_1.moneyAmountSchema,
    final_amount: common_schemas_1.moneyAmountSchema,
    cashier_code: zod_1.z.string(),
    cashier_name: zod_1.z.string(),
    invoice_id: zod_1.z.string().nullable(),
    note: zod_1.z.string().nullable(),
    timestamp: timestamp_schemas_1.timestampIsoSchema,
    is_refunded: zod_1.z.boolean(),
});
// GET /merchants/:merchantId/redemption/history - Response
exports.listRedemptionsResponseSchema = (0, api_schemas_1.listResponseSchema)(_RedemptionItemSchema);
// POST /merchants/:merchantId/redemption/:redemptionId/refund - Body (FR88-94)
//
// REFUND RULES:
// - Can refund from Transaction History page (FR88)
// - Requires reason (FR89)
// - V1: Commission is immediate, no pending cancel. V2: If within 3 hours, pending commission auto-cancelled (FR90)
// - Merchant chooses: restore coupon OR keep used (FR91-92)
// - Cannot exceed original amount (FR93)
//
exports.createRefundBodySchema = zod_1.z.object({
    reason: zod_1.z.string().min(1).max(500),
    restore_coupon: zod_1.z.boolean(), // FR91-92: true = restore to wallet, false = keep used
    refund_amount: common_schemas_1.moneyAmountSchema.positive().optional(), // Defaults to full amount
});
// POST /merchants/:merchantId/redemption/:redemptionId/refund - Response
exports.createRefundResponseSchema = (0, api_schemas_1.dataResponseSchema)(zod_1.z.object({
    refund_id: zod_1.z.number().int(),
    refund_amount: common_schemas_1.moneyAmountSchema,
    coupon_restored: zod_1.z.boolean(),
    commission_cancelled: zod_1.z.boolean(), // True if within 3hr window
}));
// ============================================
// CASHIER VALIDATION API SCHEMAS (Story 7.2)
// ============================================
// Params schema for check-cashier-code endpoint
exports.checkCashierCodeParamsSchema = _MerchantParamsSchema;
// POST /merchants/:merchantId/redemption/validate-cashier - Body
exports.checkCashierCodeBodySchema = zod_1.z.object({
    code: zod_1.z.string().length(4).regex(/^\d{4}$/, 'Code must be 4 digits'),
});
// POST /merchants/:merchantId/redemption/validate-cashier - Response
exports.checkCashierCodeResponseSchema = (0, api_schemas_1.dataResponseSchema)(zod_1.z.object({
    valid: zod_1.z.boolean(),
    cashier_id: zod_1.z.number().int().optional(),
    cashier_name: zod_1.z.string().optional(),
    error_code: zod_1.z.enum(['INVALID_CODE', 'DISABLED_CODE']).optional(),
}));
// ============================================
// COUPON VALIDATION API SCHEMAS (Story 7.3)
// ============================================
// Params schema for check-coupon-code endpoint
exports.checkCouponCodeParamsSchema = _MerchantParamsSchema;
// POST /merchants/:merchantId/redemption/validate-coupon - Body
// Unified: client sends just the code, server does lookup across both tables
exports.checkCouponCodeBodySchema = zod_1.z.object({
    code: zod_1.z.string().min(1, 'Code is required'),
});
// Internal schema for coupon details in response
const _ValidatedCouponDetailsSchema = zod_1.z.object({
    userCouponId: zod_1.z.number().int(),
    couponId: zod_1.z.number().int(),
    couponType: wallet_schemas_1.CouponTypeSchema,
    discountValue: zod_1.z.number().int().min(0).nullable(),
    itemDescription: zod_1.z.string().nullable(),
    title: zod_1.z.string(),
    merchantId: zod_1.z.number().int(),
    merchantName: zod_1.z.string(),
    customerId: zod_1.z.string().uuid(),
    customerName: zod_1.z.string().nullable(),
    expiresAt: timestamp_schemas_1.timestampIsoSchema.nullable(),
    minPurchase: common_schemas_1.moneyAmountSchema.nullable(),
    codeType: zod_1.z.enum(['self', 'shared']).optional(), // V1.2: Dual code path indicator
});
// POST /merchants/:merchantId/redemption/validate-coupon - Response
exports.checkCouponCodeResponseSchema = (0, api_schemas_1.dataResponseSchema)(zod_1.z.object({
    valid: zod_1.z.boolean(),
    coupon: _ValidatedCouponDetailsSchema.optional(),
    error_code: zod_1.z
        .enum([
        'NOT_FOUND',
        'WRONG_MERCHANT',
        'EXPIRED',
        'INVALID_FORMAT',
    ])
        .optional(),
}));
